import { inject, Injectable, signal } from "@angular/core";
import { Subject } from "rxjs";
import { NotifyService } from "@ui/notify";
import { TranslocoService } from "@jsverse/transloco";
import {
    TRANSCRIPTION_AUDIO_MIME_TYPE,
    TRANSCRIPTION_CHUNK_INTERVAL_MS,
    TRANSCRIPTION_CHUNK_QUEUE_MAX_BYTES,
    TRANSCRIPTION_CHUNK_UPLOAD_MAX_BACKOFF_MS,
    TRANSCRIPTION_CHUNK_UPLOAD_MAX_RETRIES,
    TRANSCRIPTION_CHUNK_UPLOAD_RETRY_BASE_MS,
    TRANSCRIPTION_RECORDING_BITRATE_BPS,
    TRANSCRIPTION_SAS_TOKEN_REFRESH_INTERVAL_MS,
    TRANSCRIPTION_SEGMENT_MAX_BYTES,
    TRANSCRIPTION_SEGMENT_ROLLOVER_MS,
    TRANSCRIPTION_SEGMENT_ROLLOVER_RETRY_MS,
    TRANSCRIPTION_SILENCE_DURATION_MS,
    TRANSCRIPTION_SILENCE_LEVEL_THRESHOLD,
    TRANSCRIPTION_RECORDER_FLUSH_TIMEOUT_MS,
    TRANSCRIPTION_UPLOAD_DRAIN_TIMEOUT_MS,
} from "../transcription.model";
import { SegmentAppendResult, SegmentUploadQueue } from "./segment-upload-queue";

@Injectable({ providedIn: "root" })
export class AudioCaptureService {
    private readonly _notify = inject(NotifyService);
    private readonly _t = inject(TranslocoService);

    readonly level = signal<number>(0);
    readonly noAudioDetected = signal<boolean>(false);
    readonly uploadDegraded = signal<boolean>(false);
    readonly isCapturing = signal<boolean>(false);
    readonly disconnected$ = new Subject<void>();

    private _muted = false;
    private _silentMs = 0;
    private _silenceNotified = false;

    private _micStream: MediaStream | null = null;
    private _tabStream: MediaStream | null = null;
    private _mixerCtx: AudioContext | null = null;
    private _micGain: GainNode | null = null;
    private _recorder: MediaRecorder | null = null;
    private _recordingStream: MediaStream | null = null;
    private _isCapturingAudio = false;

    private _segment: SegmentUploadQueue | null = null;
    private _drainingSegments: SegmentUploadQueue[] = [];
    private _segmentSasDeadlineAt = 0;
    private _newSegmentFn: (() => Promise<string>) | null = null;
    private _rolloverInFlight = false;
    private _rolloverCompletion: Promise<void> | null = null;
    private _rolloverRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private _stopCompletion: Promise<void> | null = null;
    private _appendPositionSupported = true;
    private _conditionalCreateSupported = true;
    private _audioLost = false;

    private _audioCtx: AudioContext | null = null;
    private _analyser: AnalyserNode | null = null;
    private _levelIntervalId: ReturnType<typeof setInterval> | null = null;

    // ─── Permission requests ────────────────────────────────────────────────

    async requestMicrophone(): Promise<"granted" | "denied"> {
        try {
            this._releaseMicStream();
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this._micStream = stream;
            stream.getAudioTracks().forEach((track) => {
                track.onended = () => {
                    if (this._isCapturingAudio) {
                        this.disconnected$.next();
                    }
                };
            });
            return "granted";
        } catch {
            return "denied";
        }
    }

    async requestTabCapture(): Promise<"granted" | "denied"> {
        try {
            this._releaseTabStream();
            const stream = await navigator.mediaDevices.getDisplayMedia({ audio: true });
            this._tabStream = stream;
            return "granted";
        } catch {
            return "denied";
        }
    }

    // ─── Recording lifecycle ────────────────────────────────────────────────

    async beginCapture(sasUrl: string, newSegmentFn?: () => Promise<string>): Promise<void> {
        await this._stopPreviousCapture();
        this._newSegmentFn = newSegmentFn ?? null;
        await this._createAppendBlob(sasUrl);
        await this._startCapture(sasUrl);
    }

    async resumeCapture(
        sasUrl: string,
        newSegmentFn?: () => Promise<string>,
        options?: { newBlob?: boolean }
    ): Promise<void> {
        await this._stopPreviousCapture();
        this._newSegmentFn = newSegmentFn ?? null;
        if (options?.newBlob) {
            await this._createAppendBlob(sasUrl);
        }
        await this._startCapture(sasUrl);
    }

    private async _stopPreviousCapture(): Promise<void> {
        if (this._isCapturingAudio) {
            await this._finishCapture({ releaseStreams: false });
        }
    }

    private async _startCapture(sasUrl: string): Promise<void> {
        if (!this._micStream) {
            throw new Error("AudioCaptureService: microphone stream not available. Call requestMicrophone() first.");
        }

        this._isCapturingAudio = true;
        this.isCapturing.set(true);
        this._muted = false;
        this._segment = this._createSegmentQueue(sasUrl);
        this._recorder = this._createRecorder(this._segment);
        this._recorder.start(TRANSCRIPTION_CHUNK_INTERVAL_MS);
        this._startLevelMeter(this._micStream);
    }

    private _createSegmentQueue(sasUrl: string): SegmentUploadQueue {
        this._segmentSasDeadlineAt = Date.now() + TRANSCRIPTION_SAS_TOKEN_REFRESH_INTERVAL_MS;

        const queue = new SegmentUploadQueue({
            sasUrl,
            maxAttempts: TRANSCRIPTION_CHUNK_UPLOAD_MAX_RETRIES,
            backoffBaseMs: TRANSCRIPTION_CHUNK_UPLOAD_RETRY_BASE_MS,
            maxBackoffMs: TRANSCRIPTION_CHUNK_UPLOAD_MAX_BACKOFF_MS,
            maxQueuedBytes: TRANSCRIPTION_CHUNK_QUEUE_MAX_BYTES,
            append: (uploadUrl, chunk, appendPosition) => this._appendChunk(uploadUrl, chunk, appendPosition),
            delay: (ms) => this._delay(ms),
            onFailure: () => this._onSegmentFailure(),
        });

        if (!this._appendPositionSupported) {
            queue.disableAppendPosition();
        }

        return queue;
    }

    private _createRecorder(target: SegmentUploadQueue): MediaRecorder {
        const mimeType = MediaRecorder.isTypeSupported(TRANSCRIPTION_AUDIO_MIME_TYPE)
            ? TRANSCRIPTION_AUDIO_MIME_TYPE
            : "";

        const options: MediaRecorderOptions = {
            audioBitsPerSecond: TRANSCRIPTION_RECORDING_BITRATE_BPS,
            ...(mimeType ? { mimeType } : {}),
        };

        const recorder = new MediaRecorder(this._ensureRecordingStream(), options);

        recorder.ondataavailable = (event) => {
            if (event.data.size === 0) {
                return;
            }

            target.enqueue(event.data);

            if (target === this._segment && this._shouldRollSegment(target)) {
                void this._rollSegment();
            }
        };

        return recorder;
    }

    private _shouldRollSegment(segment: SegmentUploadQueue): boolean {
        if (!this._isCapturingAudio || !this._newSegmentFn) {
            return false;
        }

        return (
            segment.chunkCount * TRANSCRIPTION_CHUNK_INTERVAL_MS >= TRANSCRIPTION_SEGMENT_ROLLOVER_MS ||
            segment.appendedBytes + segment.queuedBytes >= TRANSCRIPTION_SEGMENT_MAX_BYTES ||
            Date.now() >= this._segmentSasDeadlineAt
        );
    }

    private _onSegmentFailure(): void {
        this._audioLost = true;
        this.uploadDegraded.set(true);
        this._notify.error(this._t.translate("patient-transcriptions.upload-chunk-error"));

        void this._replaceFailedSegment();
    }

    private async _replaceFailedSegment(): Promise<void> {
        await this._awaitRolloverCompletion();

        if (this._segment?.accepting) {
            return;
        }

        await this._rollSegment();
    }

    private async _rollSegment(): Promise<void> {
        if (
            this._rolloverInFlight ||
            this._rolloverRetryTimeoutId !== null ||
            !this._isCapturingAudio ||
            !this._newSegmentFn ||
            !this._recorder
        ) {
            return;
        }

        this._rolloverInFlight = true;
        this._rolloverCompletion = this._performRollover();

        try {
            await this._rolloverCompletion;
        } finally {
            this._rolloverInFlight = false;
            this._rolloverCompletion = null;
        }
    }

    private async _awaitRolloverCompletion(): Promise<void> {
        const completion = this._rolloverCompletion;
        if (!completion) {
            return;
        }

        await Promise.race([
            completion.catch(() => undefined),
            this._delay(TRANSCRIPTION_UPLOAD_DRAIN_TIMEOUT_MS),
        ]);
    }

    private async _performRollover(): Promise<void> {
        let sasUrl: string;
        try {
            sasUrl = await this._newSegmentFn!();
            await this._createAppendBlob(sasUrl);
        } catch {
            this.uploadDegraded.set(true);
            this._scheduleRolloverRetry();
            return;
        }

        if (!this._isCapturingAudio || !this._recorder) {
            return;
        }

        const previousSegment = this._segment;
        const previousRecorder = this._recorder;

        this._segment = this._createSegmentQueue(sasUrl);
        this._trackPendingSegment(previousSegment);

        await this._restartRecorderAfter(previousRecorder);

        this._drainFlushedSegment(previousSegment);
    }

    private _trackPendingSegment(segment: SegmentUploadQueue | null): void {
        if (segment) {
            this._drainingSegments.push(segment);
        }
    }

    private _drainFlushedSegment(segment: SegmentUploadQueue | null): void {
        if (!segment) {
            return;
        }

        if (segment.failure) {
            segment.abandon();
            this._untrackSegment(segment);
            return;
        }

        void segment.drain().then(() => this._untrackSegment(segment));
    }

    private _untrackSegment(segment: SegmentUploadQueue): void {
        this._drainingSegments = this._drainingSegments.filter((s) => s !== segment);
    }

    private _restartRecorderAfter(previousRecorder: MediaRecorder): Promise<void> {
        return new Promise((resolve) => {
            previousRecorder.onstop = () => {
                try {
                    if (this._isCapturingAudio && this._segment) {
                        this._recorder = this._createRecorder(this._segment);
                        this._recorder.start(TRANSCRIPTION_CHUNK_INTERVAL_MS);
                        this.uploadDegraded.set(this._audioLost);
                    }
                } finally {
                    resolve();
                }
            };

            previousRecorder.stop();
        });
    }

    private _scheduleRolloverRetry(): void {
        this._clearRolloverRetry();
        this._rolloverRetryTimeoutId = setTimeout(() => {
            this._rolloverRetryTimeoutId = null;
            void this._rollSegment();
        }, TRANSCRIPTION_SEGMENT_ROLLOVER_RETRY_MS);
    }

    private _clearRolloverRetry(): void {
        if (this._rolloverRetryTimeoutId !== null) {
            clearTimeout(this._rolloverRetryTimeoutId);
            this._rolloverRetryTimeoutId = null;
        }
    }

    pause(): void {
        if (this._recorder?.state === "recording") {
            this._recorder.pause();
        }
        this._stopLevelMeter();
    }

    resume(): void {
        if (this._recorder?.state === "paused") {
            this._recorder.resume();
        }
        if (this._micStream) {
            this._startLevelMeter(this._micStream);
        }
    }

    mute(muted: boolean): void {
        this._muted = muted;
        this._micStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
        // A MediaStreamAudioSourceNode ignores track.enabled, so when the mic is mixed with tab
        // audio the disabled track still bleeds through — zero the gain instead (see _buildCombinedStream).
        if (this._micGain) {
            this._micGain.gain.value = muted ? 0 : 1;
        }
        if (muted) {
            // Deliberate mute zeroes the level — never warn about it.
            this.level.set(0);
            this._resetSilenceTracking();
        }
    }

    stop(): Promise<void> {
        this._stopCompletion ??= this._finishCaptureAndReleaseStreams();
        return this._stopCompletion;
    }

    private async _finishCaptureAndReleaseStreams(): Promise<void> {
        try {
            await this._finishCapture({ releaseStreams: true });
        } finally {
            this._stopCompletion = null;
        }
    }

    release(): void {
        this._isCapturingAudio = false;
        this.isCapturing.set(false);
        this._abandonAllSegments();
        this._cleanup();
    }

    private async _finishCapture(options: { releaseStreams: boolean }): Promise<void> {
        this._isCapturingAudio = false;
        this.isCapturing.set(false);
        this._clearRolloverRetry();

        await this._awaitRolloverCompletion();
        await this._stopRecorder();
        await this._drainAllSegments();

        this._stopLevelMeter();
        this._closeMixer();
        this._resetCaptureState();

        if (options.releaseStreams) {
            this._releaseMicStream();
            this._releaseTabStream();
        }
    }

    private async _stopRecorder(): Promise<void> {
        const recorder = this._recorder;
        if (!recorder) {
            return;
        }

        if (recorder.state === "inactive") {
            await this._awaitPendingRecorderFlush(recorder);
            return;
        }

        await new Promise<void>((resolve) => {
            recorder.onstop = () => resolve();
            recorder.stop();
        });
    }

    private _awaitPendingRecorderFlush(recorder: MediaRecorder): Promise<void> {
        return Promise.race([
            new Promise<void>((resolve) => {
                recorder.onstop = () => resolve();
            }),
            this._delay(TRANSCRIPTION_RECORDER_FLUSH_TIMEOUT_MS),
        ]);
    }

    private async _drainAllSegments(): Promise<void> {
        const pending = [this._segment, ...this._drainingSegments].filter(
            (segment): segment is SegmentUploadQueue => segment !== null
        );

        if (pending.length === 0) {
            return;
        }

        await Promise.race([
            Promise.all(pending.map((segment) => segment.drain())),
            this._delay(TRANSCRIPTION_UPLOAD_DRAIN_TIMEOUT_MS).then(() => {
                pending.forEach((segment) => segment.abandon());
            }),
        ]);
    }

    private _abandonAllSegments(): void {
        this._segment?.abandon();
        this._drainingSegments.forEach((segment) => segment.abandon());
        this._drainingSegments = [];
    }

    // ─── Azure Blob upload ──────────────────────────────────────────────────

    private async _createAppendBlob(sasUrl: string): Promise<void> {
        try {
            await this._putAppendBlob(sasUrl, this._conditionalCreateSupported);
        } catch (err) {
            if (this._conditionalCreateSupported && err instanceof TypeError) {
                this._conditionalCreateSupported = false;
                try {
                    await this._putAppendBlob(sasUrl, false);
                    return;
                } catch {
                    this._notify.error(this._t.translate("patient-transcriptions.upload-error"));
                    throw err;
                }
            }

            this._notify.error(this._t.translate("patient-transcriptions.upload-error"));
            throw err;
        }
    }

    private async _putAppendBlob(sasUrl: string, conditional: boolean): Promise<void> {
        const headers: Record<string, string> = { "x-ms-blob-type": "AppendBlob" };
        if (conditional) {
            headers["If-None-Match"] = "*";
        }

        const response = await fetch(sasUrl, { method: "PUT", headers });
        if (!response.ok) {
            throw new Error(`CreateAppendBlob failed: ${response.status}`);
        }
    }

    private async _appendChunk(
        uploadUrl: string,
        chunk: Blob,
        appendPosition: number | null
    ): Promise<SegmentAppendResult> {
        try {
            return await this._putAppendBlock(uploadUrl, chunk, appendPosition);
        } catch (err) {
            if (appendPosition === null || !(err instanceof TypeError)) {
                throw err;
            }

            const result = await this._putAppendBlock(uploadUrl, chunk, null);
            this._appendPositionSupported = false;
            this._segment?.disableAppendPosition();
            return result;
        }
    }

    private async _putAppendBlock(
        uploadUrl: string,
        chunk: Blob,
        appendPosition: number | null
    ): Promise<SegmentAppendResult> {
        const headers: Record<string, string> = { "Content-Type": "application/octet-stream" };
        if (appendPosition !== null) {
            headers["x-ms-blob-condition-appendpos"] = String(appendPosition);
        }

        const response = await fetch(uploadUrl, { method: "PUT", headers, body: chunk });

        if (response.status === 412) {
            return "already-applied";
        }

        if (!response.ok) {
            throw new Error(`AppendBlock failed: ${response.status}`);
        }

        return "applied";
    }

    private _delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private _ensureRecordingStream(): MediaStream {
        if (this._recordingStream) {
            return this._recordingStream;
        }

        const tabTracks = this._tabStream?.getAudioTracks() ?? [];
        if (tabTracks.length === 0) {
            this._recordingStream = this._micStream!;
            return this._recordingStream;
        }

        this._mixerCtx = new AudioContext();
        const dest = this._mixerCtx.createMediaStreamDestination();
        this._micGain = this._mixerCtx.createGain();
        this._micGain.gain.value = this._muted ? 0 : 1;
        this._mixerCtx.createMediaStreamSource(this._micStream!).connect(this._micGain).connect(dest);
        this._mixerCtx.createMediaStreamSource(this._tabStream!).connect(dest);
        this._recordingStream = dest.stream;
        return this._recordingStream;
    }

    // ─── VU meter ───────────────────────────────────────────────────────────

    private _startLevelMeter(stream: MediaStream): void {
        this._stopLevelMeter();

        try {
            this._audioCtx = new AudioContext();
            this._analyser = this._audioCtx.createAnalyser();
            this._analyser.fftSize = 256;

            const source = this._audioCtx.createMediaStreamSource(stream);
            source.connect(this._analyser);

            const buffer = new Uint8Array(this._analyser.frequencyBinCount);

            this._levelIntervalId = setInterval(() => {
                this._analyser!.getByteFrequencyData(buffer);
                const avg = buffer.reduce((sum, v) => sum + v, 0) / buffer.length;
                const level = Math.min(100, Math.round((avg / 255) * 100));
                this.level.set(level);
                this._evaluateSilence(level, 100);
            }, 100);
        } catch {
            // AudioContext not available (e.g. in tests) — silently skip
        }
    }

    /**
     * Accumulates near-zero level into a silence timer and raises {@link noAudioDetected}
     * once it crosses the threshold, firing the snackbar once per silence episode. Skipped
     * while muted (deliberate silence). `elapsedMs` is the tick interval, injectable for tests.
     */
    private _evaluateSilence(level: number, elapsedMs: number): void {
        if (this._muted) {
            return;
        }

        if (level <= TRANSCRIPTION_SILENCE_LEVEL_THRESHOLD) {
            this._silentMs += elapsedMs;
            if (this._silentMs >= TRANSCRIPTION_SILENCE_DURATION_MS && !this._silenceNotified) {
                this._silenceNotified = true;
                this.noAudioDetected.set(true);
                this._notify.warn(this._t.translate("patient-transcriptions.no-audio-snackbar"));
            }
            return;
        }

        this._resetSilenceTracking();
    }

    private _resetSilenceTracking(): void {
        this._silentMs = 0;
        this._silenceNotified = false;
        this.noAudioDetected.set(false);
    }

    private _stopLevelMeter(): void {
        if (this._levelIntervalId !== null) {
            clearInterval(this._levelIntervalId);
            this._levelIntervalId = null;
        }
        this.level.set(0);
        this._resetSilenceTracking();
        this._analyser = null;

        if (this._audioCtx) {
            this._audioCtx.close().catch(() => {});
            this._audioCtx = null;
        }
    }

    // ─── Internal cleanup ───────────────────────────────────────────────────

    private _releaseMicStream(): void {
        this._micStream?.getTracks().forEach((t) => t.stop());
        this._micStream = null;
    }

    private _releaseTabStream(): void {
        this._tabStream?.getTracks().forEach((t) => t.stop());
        this._tabStream = null;
    }

    private _closeMixer(): void {
        if (this._mixerCtx) {
            this._mixerCtx.close().catch(() => {});
            this._mixerCtx = null;
        }
        this._micGain = null;
    }

    private _resetCaptureState(): void {
        this._newSegmentFn = null;
        this._recorder = null;
        this._recordingStream = null;
        this._segment = null;
        this._drainingSegments = [];
        this._rolloverInFlight = false;
        this._rolloverCompletion = null;
        this._segmentSasDeadlineAt = 0;
        this._audioLost = false;
        this.uploadDegraded.set(false);
    }

    private _cleanup(): void {
        this._clearRolloverRetry();
        this._stopLevelMeter();
        this._releaseMicStream();
        this._releaseTabStream();
        this._closeMixer();
        this._resetCaptureState();
    }
}
